/**
 * FP3-034 — Student Goal Plan tab (MVP).
 *
 * Planning layer between raw evidence and annual goal creation.
 * Renders:
 *   - Top summary chip row (counts)
 *   - Need Areas section (provider-controlled)
 *   - Current Focus / Active Goals row (mixes real targets +
 *     virtual targets surfaced from existing active goals)
 *   - Future / Parked Targets
 *   - Achieved / Previous Targets (mixes real + auto-surfaced
 *     achieved annual goals)
 *   - Suggested Next Steps (AI-driven, DRAFT only)
 *
 * Edit permission (MVP — admin + primary assigned only) is
 * enforced server-side. The tab uses `viewer_can_edit` returned by
 * `/goal-plan` GET to hide edit buttons for view-only roles.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Sparkle,
  Plus,
  PencilSimple,
  TrashSimple,
  Target,
  Flag,
  Trophy,
  CheckCircle,
  Stack,
  WarningCircle,
  Link as LinkIcon,
  Lightbulb,
} from "@phosphor-icons/react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
// FP3-035F Issue 2 — Preserve ?as_provider= on internal goal navigations.
import useShadowNavigate from "@/lib/useShadowNavigate";

const TARGET_STATUS_LABEL = {
  suggested: "Suggested",
  current_focus: "Current Focus",
  active_goal: "Active Goal",
  parked: "Parked",
  achieved: "Achieved",
  discontinued: "Discontinued",
  needs_current_data: "Needs Current Data",
};

const TARGET_STATUS_TONE = {
  suggested: "border-[#D8C99B] bg-[#FDF8E7] text-[#7A5B1F]",
  current_focus: "border-[#A8C6B5] bg-[#E8F0EB] text-[#1E3F33]",
  active_goal: "border-[#B6CBC0] bg-[#EFF4F1] text-[#1E3F33]",
  parked: "border-[#C8C5BB] bg-[#F1EFE7] text-[#6B6457]",
  achieved: "border-[#B6CBC0] bg-[#EFF4F1] text-[#2F6B4F]",
  discontinued: "border-[#D6D3CB] bg-[#F1F2F4] text-[#6B7280]",
  needs_current_data: "border-[#E9C7B4] bg-[#FBEFE6] text-[#8A4A21]",
};

const DOMAIN_OPTIONS = [
  ["speech_language", "Speech / Language"],
  ["academic", "Academic"],
  ["occupational_therapy", "Occupational Therapy"],
  ["physical_therapy", "Physical Therapy"],
  ["behavior", "Behavior"],
  ["counseling", "Counseling"],
  ["other", "Other"],
];

const PRIORITY_OPTIONS = [
  ["high", "High"],
  ["medium", "Medium"],
  ["low", "Low"],
];

export default function StudentGoalPlanTab({ studentId }) {
  const navigate = useShadowNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [naModalOpen, setNaModalOpen] = useState(false);
  const [editingNa, setEditingNa] = useState(null);
  const [targetModalOpen, setTargetModalOpen] = useState(false);
  const [editingTarget, setEditingTarget] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  // FP3-034A — bumped every time the AI modal opens so the picker
  // remounts and re-fetches the documents list (catches docs uploaded
  // in another tab while the workspace was open).
  const [aiOpenCount, setAiOpenCount] = useState(0);
  // FP3-035 correction — when the Assessments tab "Use as evidence in
  // Goal Plan" affordance opens this page with `?suggest-run=<id>`,
  // we forward that id as the AI modal's `initialRunIds` so the
  // matching row opens pre-checked.
  const [initialRunIds, setInitialRunIds] = useState([]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const rid = params.get("suggest-run");
    if (rid) {
      setInitialRunIds([rid]);
      setAiOpenCount((c) => c + 1);
      setAiOpen(true);
      // Drop the param from the URL so refreshing the page later
      // doesn't keep re-opening the modal.
      params.delete("suggest-run");
      const next = params.toString();
      const url = `${window.location.pathname}${next ? `?${next}` : ""}`;
      window.history.replaceState({}, "", url);
    }
  }, []);
  const [pendingDeleteTarget, setPendingDeleteTarget] = useState(null);
  const [pendingDeleteNa, setPendingDeleteNa] = useState(null);

  const reload = async ({ background = false } = {}) => {
    if (!background) setLoading(true);
    setError("");
    try {
      const r = await api.getGoalPlan(studentId);
      setData(r.data);
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 403
          ? "You don't have access to this student's goal plan."
          : err?.response?.data?.detail || "Could not load goal plan"
      );
    } finally {
      if (!background) setLoading(false);
    }
  };

  useEffect(() => {
    if (!studentId) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const canEdit = !!data?.viewer_can_edit;
  const counts = data?.counts || {};
  const allTargets = useMemo(
    () => [...(data?.targets || []), ...(data?.virtual_targets || [])],
    [data]
  );
  const needAreas = data?.plan?.need_areas || [];

  const groupByStatus = (status) =>
    allTargets.filter((t) => t.status === status);

  const onCreateGoalFromTarget = async (target) => {
    if (target.is_virtual) {
      // Already an annual goal — open it in GoalProgress for editing.
      navigate(`/progress/goal/${target.linked_goal_id}`);
      return;
    }
    // FP3-034C — If the provider already created a goal from this
    // target previously, jump straight to that goal's progress
    // page instead of forcing them through Build a Goal again.
    if (target.linked_goal_id) {
      navigate(`/progress/goal/${target.linked_goal_id}`);
      return;
    }
    // Build prefill query string and route to Add Goal. GoalBuilder
    // reads these params (FP3-034 prefill plumbing) so the form
    // opens ready to review/edit; no auto-save.
    const qs = new URLSearchParams({
      studentId: studentId,
      planTargetId: target.id,
      prefillGoalArea: target.service_area || "",
      prefillTargetSkill: target.target_skill || "",
      prefillInstructionalLevel: target.instructional_level || "",
      prefillRationale: target.rationale || "",
    });
    navigate(`/goals/new?${qs.toString()}`);
  };

  if (loading) {
    return (
      <div className="text-[12.5px] text-[#6B7280] p-6">Loading goal plan…</div>
    );
  }
  if (error) {
    return (
      <div
        className="text-[12.5px] text-[#7A3A2C] bg-[#F8E8E2] border border-[#E8C7BC] rounded-md p-3"
        data-testid="goal-plan-error"
      >
        {error}
      </div>
    );
  }

  const isEmpty =
    needAreas.length === 0 &&
    (data?.targets?.length || 0) === 0 &&
    (data?.virtual_targets?.length || 0) === 0;

  return (
    <div className="space-y-5" data-testid="goal-plan-tab">
      {/* Top summary chip row */}
      <Card className="border-[#F1F2F4]">
        <CardContent className="py-3 px-4">
          <div className="flex items-center gap-3 flex-wrap">
            <SummaryChip
              icon={Target}
              label="Current Focus"
              value={counts.current_focus || 0}
              testid="goal-plan-count-current-focus"
            />
            <SummaryChip
              icon={Stack}
              label="Active Goals"
              value={counts.active_goal || 0}
              testid="goal-plan-count-active-goal"
            />
            <SummaryChip
              icon={Flag}
              label="Parked"
              value={counts.parked || 0}
              testid="goal-plan-count-parked"
            />
            <SummaryChip
              icon={Trophy}
              label="Achieved"
              value={counts.achieved || 0}
              testid="goal-plan-count-achieved"
            />
            <SummaryChip
              icon={WarningCircle}
              label="Needs current data"
              value={counts.needs_current_data || 0}
              testid="goal-plan-count-needs-current-data"
            />
            <div className="flex-1" />
            {canEdit && (
              <>
                <Button
                  type="button"
                  data-testid="goal-plan-add-need-area-btn"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditingNa(null);
                    setNaModalOpen(true);
                  }}
                  className="h-8 text-[12px] border-[#E5E3DB]"
                >
                  <Plus size={12} className="mr-1" />
                  {/* FP3-034C — User-facing rename: provider-facing
                      label is now "long-term area" (broader
                      developmental framing) but the underlying API
                      object remains `need_area` to avoid migration. */}
                  Long-term area
                </Button>
                <Button
                  type="button"
                  data-testid="goal-plan-add-target-btn"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditingTarget(null);
                    setTargetModalOpen(true);
                  }}
                  className="h-8 text-[12px] border-[#E5E3DB]"
                >
                  <Plus size={12} className="mr-1" />
                  Target
                </Button>
                <Button
                  type="button"
                  data-testid="goal-plan-ai-suggest-btn"
                  size="sm"
                  onClick={() => {
                    setAiOpenCount((c) => c + 1);
                    setAiOpen(true);
                  }}
                  className="h-8 text-[12px] bg-[#1E3F33] hover:bg-[#173127] text-white"
                >
                  <Sparkle size={12} className="mr-1" weight="duotone" />
                  Suggest with evidence
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {isEmpty && (
        <Card
          className="border-dashed border-[#E5E3DB] bg-[#FBFAF6]"
          data-testid="goal-plan-empty-state"
        >
          <CardContent className="py-8 px-6 text-center">
            <Lightbulb
              size={20}
              weight="duotone"
              className="text-[#1E3F33] mx-auto mb-2"
            />
            <p className="text-[14px] font-semibold text-[#1F2937] heading-font">
              No goal plan has been started yet.
            </p>
            <p className="text-[11.5px] text-[#6B7280] mt-1">
              Start by adding a long-term development area, drafting a
              target, or asking AI for suggestions. Nothing here
              changes annual goals automatically.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Long Term Development (formerly "Need Areas") */}
      <section data-testid="goal-plan-section-need-areas">
        <h3 className="text-[11px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold mb-2">
          Long Term Development
        </h3>
        {needAreas.length === 0 ? (
          <p className="text-[12px] text-[#9B9890]">No long-term development areas yet.</p>
        ) : (
          <div className="grid gap-2 md:grid-cols-2">
            {needAreas.map((na) => (
              <NeedAreaCard
                key={na.id}
                na={na}
                canEdit={canEdit}
                onEdit={() => {
                  setEditingNa(na);
                  setNaModalOpen(true);
                }}
                onDelete={() => setPendingDeleteNa(na)}
                targetCount={allTargets.filter((t) => t.need_area_id === na.id).length}
              />
            ))}
          </div>
        )}
      </section>

      {/* Current Focus */}
      <TargetSection
        title="Current Focus"
        testidKey="current-focus"
        targets={[
          ...groupByStatus("current_focus"),
          ...groupByStatus("active_goal"),
        ]}
        emptyLabel="No targets in current focus."
        canEdit={canEdit}
        onEdit={(t) => {
          setEditingTarget(t);
          setTargetModalOpen(true);
        }}
        onDelete={(t) => setPendingDeleteTarget(t)}
        onCreateGoal={onCreateGoalFromTarget}
        needAreas={needAreas}
      />

      {/* Future / Parked */}
      <TargetSection
        title="Future / Parked Targets"
        testidKey="parked"
        targets={[
          ...groupByStatus("parked"),
          ...groupByStatus("needs_current_data"),
          ...groupByStatus("suggested"),
        ]}
        emptyLabel="No parked targets."
        canEdit={canEdit}
        onEdit={(t) => {
          setEditingTarget(t);
          setTargetModalOpen(true);
        }}
        onDelete={(t) => setPendingDeleteTarget(t)}
        onCreateGoal={onCreateGoalFromTarget}
        needAreas={needAreas}
      />

      {/* Achieved / Previous */}
      <TargetSection
        title="Achieved / Previous Targets"
        testidKey="achieved"
        targets={[
          ...groupByStatus("achieved"),
          ...groupByStatus("discontinued"),
        ]}
        emptyLabel="No achieved or previous targets."
        canEdit={canEdit}
        onEdit={(t) => {
          setEditingTarget(t);
          setTargetModalOpen(true);
        }}
        onDelete={(t) => setPendingDeleteTarget(t)}
        onCreateGoal={onCreateGoalFromTarget}
        needAreas={needAreas}
      />

      {/* Modals */}
      {naModalOpen && (
        <NeedAreaModal
          studentId={studentId}
          editing={editingNa}
          onClose={() => {
            setNaModalOpen(false);
            setEditingNa(null);
          }}
          onSaved={async () => {
            setNaModalOpen(false);
            setEditingNa(null);
            await reload({ background: true });
          }}
        />
      )}
      {targetModalOpen && (
        <TargetModal
          studentId={studentId}
          editing={editingTarget}
          needAreas={needAreas}
          onClose={() => {
            setTargetModalOpen(false);
            setEditingTarget(null);
          }}
          onSaved={async () => {
            setTargetModalOpen(false);
            setEditingTarget(null);
            await reload({ background: true });
          }}
        />
      )}
      {aiOpen && (
        <AISuggestionsModal
          // FP3-034A — `key` bumps each time the provider opens the
          // modal, so the document picker re-fetches the latest
          // documents list (catches docs uploaded in another tab
          // while the workspace was open).
          key={`ai-${aiOpenCount}`}
          studentId={studentId}
          needAreas={needAreas}
          initialRunIds={initialRunIds}
          onClose={() => {
            setAiOpen(false);
            setInitialRunIds([]);
          }}
          onAccepted={async () => {
            await reload({ background: true });
          }}
        />
      )}

      <ConfirmDialog
        open={!!pendingDeleteTarget}
        onOpenChange={(o) => setPendingDeleteTarget(o ? pendingDeleteTarget : null)}
        title="Delete this target?"
        description={`"${pendingDeleteTarget?.label || ""}" will be removed from the goal plan. The linked annual goal (if any) is NOT affected.`}
        confirmLabel="Delete target"
        destructive
        onConfirm={async () => {
          try {
            await api.deleteGoalPlanTarget(studentId, pendingDeleteTarget.id);
            toast.success("Target deleted");
            setPendingDeleteTarget(null);
            await reload({ background: true });
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not delete");
          }
        }}
        testId="goal-plan-target-delete-confirm"
      />
      <ConfirmDialog
        open={!!pendingDeleteNa}
        onOpenChange={(o) => setPendingDeleteNa(o ? pendingDeleteNa : null)}
        title="Delete this long-term development area?"
        description={`"${pendingDeleteNa?.label || ""}" will be removed. Targets in this area are kept but lose the link.`}
        confirmLabel="Delete area"
        destructive
        onConfirm={async () => {
          try {
            await api.deleteGoalPlanNeedArea(studentId, pendingDeleteNa.id);
            toast.success("Long-term development area deleted");
            setPendingDeleteNa(null);
            await reload({ background: true });
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not delete");
          }
        }}
        testId="goal-plan-need-area-delete-confirm"
      />
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────

function SummaryChip({ icon: Icon, label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1.5 rounded-full bg-[#F9F8F6] border border-[#E5E3DB]"
    >
      <Icon size={12} weight="duotone" className="text-[#1E3F33]" />
      <span className="text-[#6B7280]">{label}</span>
      <span className="font-bold text-[#1F2937] tabular-nums">{value}</span>
    </div>
  );
}

function NeedAreaCard({ na, canEdit, onEdit, onDelete, targetCount }) {
  return (
    <div
      data-testid={`goal-plan-need-area-${na.id}`}
      className="rounded-lg border border-[#E5E3DB] bg-white p-3 hover:border-[#D8D5CC]"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-[#1F2937]">
            {na.label}
          </div>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap text-[10.5px] text-[#6B7280]">
            <span className="uppercase tracking-[0.08em] font-medium">
              {(na.provider_domain || "other").replace(/_/g, " ")}
            </span>
            <span>·</span>
            <span className="uppercase tracking-[0.08em] font-medium">
              {na.priority}
            </span>
            <span>·</span>
            <span>{targetCount} target{targetCount === 1 ? "" : "s"}</span>
          </div>
          {na.description && (
            <p className="text-[11.5px] text-[#4B5563] mt-1.5">{na.description}</p>
          )}
          {na.evidence_summary && (
            <p className="text-[11.5px] text-[#6B7280] mt-1 italic">
              {na.evidence_summary}
            </p>
          )}
        </div>
        {canEdit && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              data-testid={`goal-plan-need-area-edit-${na.id}`}
              onClick={onEdit}
              aria-label="Edit long-term development area"
              className="text-[#6B7280] hover:text-[#1E3F33] p-1.5 rounded-md hover:bg-[#E8F0EB]"
            >
              <PencilSimple size={12} />
            </button>
            <button
              type="button"
              data-testid={`goal-plan-need-area-delete-${na.id}`}
              onClick={onDelete}
              aria-label="Delete long-term development area"
              className="text-[#6B7280] hover:text-[#7A3A2C] p-1.5 rounded-md hover:bg-[#F8E8E2]"
            >
              <TrashSimple size={12} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function TargetSection({
  title,
  testidKey,
  targets,
  emptyLabel,
  canEdit,
  onEdit,
  onDelete,
  onCreateGoal,
  needAreas,
}) {
  return (
    <section data-testid={`goal-plan-section-${testidKey}`}>
      <h3 className="text-[11px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold mb-2">
        {title}
      </h3>
      {targets.length === 0 ? (
        <p className="text-[12px] text-[#9B9890]" data-testid={`goal-plan-section-${testidKey}-empty`}>
          {emptyLabel}
        </p>
      ) : (
        <div className="space-y-2">
          {targets.map((t) => (
            <TargetCard
              key={t.id}
              t={t}
              canEdit={canEdit && !t.is_virtual}
              onEdit={() => onEdit(t)}
              onDelete={() => onDelete(t)}
              onCreateGoal={() => onCreateGoal(t)}
              needAreaLabel={
                (needAreas.find((na) => na.id === t.need_area_id) || {}).label
              }
            />
          ))}
        </div>
      )}
    </section>
  );
}

function TargetCard({ t, canEdit, onEdit, onDelete, onCreateGoal, needAreaLabel }) {
  const tone = TARGET_STATUS_TONE[t.status] || TARGET_STATUS_TONE.suggested;
  const isVirtual = !!t.is_virtual;
  return (
    <div
      data-testid={`goal-plan-target-${t.id}`}
      className="rounded-lg border border-[#E5E3DB] bg-white p-3 hover:border-[#D8D5CC]"
    >
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span
              data-testid={`goal-plan-target-status-${t.id}`}
              className={`text-[10px] uppercase tracking-[0.06em] font-semibold rounded-full px-1.5 py-0.5 border ${tone}`}
            >
              {TARGET_STATUS_LABEL[t.status] || t.status}
            </span>
            {isVirtual && (
              <span className="text-[10px] uppercase tracking-[0.06em] font-semibold rounded-full px-1.5 py-0.5 border border-[#D6D3CB] bg-[#F1F2F4] text-[#6B7280]">
                from annual goal
              </span>
            )}
            {t.service_area && (
              <span className="text-[10px] text-[#6B7280]">
                {t.service_area}
              </span>
            )}
            {needAreaLabel && (
              <span className="text-[10px] text-[#6B7280]">
                · {needAreaLabel}
              </span>
            )}
          </div>
          <div
            data-testid={`goal-plan-target-label-${t.id}`}
            className="text-[13px] font-semibold text-[#1F2937] mt-1"
          >
            {t.label}
          </div>
          {t.target_skill && (
            <p className="text-[11.5px] text-[#4B5563] mt-0.5">
              <span className="text-[#6B7280] mr-1">Skill:</span>
              {t.target_skill}
            </p>
          )}
          {(t.instructional_level || t.date_achieved) && (
            <p className="text-[10.5px] text-[#6B7280] mt-0.5 flex gap-2">
              {t.instructional_level && (
                <span>Level: {t.instructional_level}</span>
              )}
              {t.date_achieved && <span>Achieved: {t.date_achieved}</span>}
            </p>
          )}
          {t.rationale && (
            <p className="text-[11.5px] text-[#4B5563] mt-1 italic">
              {t.rationale}
            </p>
          )}
          {t.related_document_ids?.length > 0 && (
            <p className="text-[10.5px] text-[#6B7280] mt-1 flex items-center gap-1">
              <LinkIcon size={10} />
              {t.related_document_ids.length} linked document
              {t.related_document_ids.length === 1 ? "" : "s"}
            </p>
          )}
          {isVirtual && t.current_mastery !== undefined && t.current_mastery !== null && (
            <p className="text-[11px] text-[#4B5563] mt-1">
              Current {t.current_mastery}% · Target {t.target_mastery ?? "—"}%
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            data-testid={`goal-plan-target-create-goal-${t.id}`}
            onClick={onCreateGoal}
            className="text-[11px] font-semibold text-[#1E3F33] hover:text-[#173127] px-2 py-1 rounded-md border border-[#E5E3DB] hover:bg-[#E8F0EB]"
          >
            {/* FP3-034C — If the target is a virtual annual goal OR
                the provider has already created a goal from this
                Goal Plan target, surface "Open goal" so they jump
                straight to the live goal. */}
            {isVirtual || t.linked_goal_id ? "Open goal" : "Create goal"}
          </button>
          {canEdit && (
            <>
              <button
                type="button"
                data-testid={`goal-plan-target-edit-${t.id}`}
                onClick={onEdit}
                aria-label="Edit target"
                className="text-[#6B7280] hover:text-[#1E3F33] p-1.5 rounded-md hover:bg-[#E8F0EB]"
              >
                <PencilSimple size={12} />
              </button>
              <button
                type="button"
                data-testid={`goal-plan-target-delete-${t.id}`}
                onClick={onDelete}
                aria-label="Delete target"
                className="text-[#6B7280] hover:text-[#7A3A2C] p-1.5 rounded-md hover:bg-[#F8E8E2]"
              >
                <TrashSimple size={12} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Need Area modal ──────────────────────────────────────────────────

function NeedAreaModal({ studentId, editing, onClose, onSaved }) {
  const isEdit = !!editing;
  const [label, setLabel] = useState(editing?.label || "");
  const [domain, setDomain] = useState(editing?.provider_domain || "speech_language");
  const [priority, setPriority] = useState(editing?.priority || "medium");
  const [description, setDescription] = useState(editing?.description || "");
  const [evidence, setEvidence] = useState(editing?.evidence_summary || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!label.trim()) {
      toast.error("Long-term area label is required");
      return;
    }
    setBusy(true);
    try {
      if (isEdit) {
        await api.updateGoalPlanNeedArea(studentId, editing.id, {
          label, provider_domain: domain, priority, description, evidence_summary: evidence,
        });
      } else {
        await api.createGoalPlanNeedArea(studentId, {
          label, provider_domain: domain, priority, description, evidence_summary: evidence,
        });
      }
      toast.success(isEdit ? "Long-term area updated" : "Long-term area added");
      await onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid="goal-plan-need-area-modal"
        aria-describedby={undefined}
        className="max-w-md max-h-[90vh] overflow-y-auto p-0 border-[#E5E3DB] rounded-2xl"
      >
        <header className="px-5 pt-5 pb-3 border-b border-[#F1F2F4]">
          <DialogTitle className="text-[15px] font-semibold text-[#1F2937] heading-font">
            {isEdit ? "Edit long-term area" : "Add long-term area"}
          </DialogTitle>
        </header>
        <div className="px-5 py-4 space-y-3.5">
          <Field label="Label" required>
            <input
              data-testid="goal-plan-need-area-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full text-[13px] border border-[#E5E3DB] rounded-md px-2 py-1.5"
            />
          </Field>
          <Field label="Domain">
            <select
              data-testid="goal-plan-need-area-domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              className="w-full text-[13px] border border-[#E5E3DB] rounded-md px-2 py-1.5"
            >
              {DOMAIN_OPTIONS.map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </Field>
          <Field label="Priority">
            <select
              data-testid="goal-plan-need-area-priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="w-full text-[13px] border border-[#E5E3DB] rounded-md px-2 py-1.5"
            >
              {PRIORITY_OPTIONS.map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </Field>
          <Field label="Description">
            <textarea
              data-testid="goal-plan-need-area-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full text-[13px] border border-[#E5E3DB] rounded-md px-2 py-1.5"
            />
          </Field>
          <Field label="Evidence summary">
            <textarea
              data-testid="goal-plan-need-area-evidence"
              value={evidence}
              onChange={(e) => setEvidence(e.target.value)}
              rows={2}
              className="w-full text-[13px] border border-[#E5E3DB] rounded-md px-2 py-1.5"
            />
          </Field>
        </div>
        <footer className="px-5 py-3 border-t border-[#F1F2F4] flex justify-end gap-2">
          <Button
            type="button"
            data-testid="goal-plan-need-area-cancel"
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={busy}
            className="h-8 text-[12px] border-[#E5E3DB]"
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="goal-plan-need-area-submit"
            size="sm"
            onClick={submit}
            disabled={busy}
            className="h-8 text-[12px] bg-[#1E3F33] hover:bg-[#173127] text-white"
          >
            {busy ? "Saving…" : isEdit ? "Save changes" : "Add long-term area"}
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
}

// ── Target modal ─────────────────────────────────────────────────────

function TargetModal({ studentId, editing, needAreas, onClose, onSaved }) {
  const isEdit = !!editing;
  const [label, setLabel] = useState(editing?.label || "");
  const [targetSkill, setTargetSkill] = useState(editing?.target_skill || "");
  const [status, setStatus] = useState(editing?.status || "suggested");
  const [needAreaId, setNeedAreaId] = useState(editing?.need_area_id || "");
  const [instLevel, setInstLevel] = useState(editing?.instructional_level || "");
  const [serviceArea, setServiceArea] = useState(editing?.service_area || "");
  const [rationale, setRationale] = useState(editing?.rationale || "");
  const [dateAchieved, setDateAchieved] = useState(editing?.date_achieved || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!label.trim() || !targetSkill.trim()) {
      toast.error("Label and target skill are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        label,
        target_skill: targetSkill,
        status,
        need_area_id: needAreaId || null,
        instructional_level: instLevel,
        service_area: serviceArea,
        rationale,
        date_achieved: dateAchieved,
      };
      if (isEdit) {
        await api.updateGoalPlanTarget(studentId, editing.id, payload);
      } else {
        await api.createGoalPlanTarget(studentId, payload);
      }
      toast.success(isEdit ? "Target updated" : "Target added");
      await onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid="goal-plan-target-modal"
        aria-describedby={undefined}
        className="max-w-md max-h-[90vh] overflow-y-auto p-0 border-[#E5E3DB] rounded-2xl"
      >
        <header className="px-5 pt-5 pb-3 border-b border-[#F1F2F4]">
          <DialogTitle className="text-[15px] font-semibold text-[#1F2937] heading-font">
            {isEdit ? "Edit target" : "Add target"}
          </DialogTitle>
        </header>
        <div className="px-5 py-4 space-y-3.5">
          <Field label="Label" required>
            <input
              data-testid="goal-plan-target-label-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full text-[13px] border border-[#E5E3DB] rounded-md px-2 py-1.5"
            />
          </Field>
          <Field label="Target skill" required>
            <input
              data-testid="goal-plan-target-skill-input"
              value={targetSkill}
              onChange={(e) => setTargetSkill(e.target.value)}
              className="w-full text-[13px] border border-[#E5E3DB] rounded-md px-2 py-1.5"
            />
          </Field>
          <Field label="Status">
            <select
              data-testid="goal-plan-target-status-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full text-[13px] border border-[#E5E3DB] rounded-md px-2 py-1.5"
            >
              {Object.entries(TARGET_STATUS_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </Field>
          {needAreas.length > 0 && (
            <Field label="Long-term area">
              <select
                data-testid="goal-plan-target-need-area-select"
                value={needAreaId}
                onChange={(e) => setNeedAreaId(e.target.value)}
                className="w-full text-[13px] border border-[#E5E3DB] rounded-md px-2 py-1.5"
              >
                <option value="">— None —</option>
                {needAreas.map((na) => (
                  <option key={na.id} value={na.id}>{na.label}</option>
                ))}
              </select>
            </Field>
          )}
          <div className="grid grid-cols-2 gap-2.5">
            <Field label="Instructional level">
              <input
                data-testid="goal-plan-target-level"
                value={instLevel}
                onChange={(e) => setInstLevel(e.target.value)}
                className="w-full text-[13px] border border-[#E5E3DB] rounded-md px-2 py-1.5"
              />
            </Field>
            <Field label="Service area">
              <input
                data-testid="goal-plan-target-service-area"
                value={serviceArea}
                onChange={(e) => setServiceArea(e.target.value)}
                className="w-full text-[13px] border border-[#E5E3DB] rounded-md px-2 py-1.5"
              />
            </Field>
          </div>
          <Field label="Rationale">
            <textarea
              data-testid="goal-plan-target-rationale"
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              rows={2}
              className="w-full text-[13px] border border-[#E5E3DB] rounded-md px-2 py-1.5"
            />
          </Field>
          {status === "achieved" && (
            <Field label="Date achieved">
              <input
                type="date"
                data-testid="goal-plan-target-date-achieved"
                value={dateAchieved}
                onChange={(e) => setDateAchieved(e.target.value)}
                className="w-full text-[13px] border border-[#E5E3DB] rounded-md px-2 py-1.5"
              />
            </Field>
          )}
        </div>
        <footer className="px-5 py-3 border-t border-[#F1F2F4] flex justify-end gap-2">
          <Button
            type="button"
            data-testid="goal-plan-target-cancel"
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={busy}
            className="h-8 text-[12px] border-[#E5E3DB]"
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="goal-plan-target-submit"
            size="sm"
            onClick={submit}
            disabled={busy}
            className="h-8 text-[12px] bg-[#1E3F33] hover:bg-[#173127] text-white"
          >
            {busy ? "Saving…" : isEdit ? "Save changes" : "Add target"}
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
}

// ── AI Suggestions modal ─────────────────────────────────────────────

const ANALYZABLE_EXT = new Set(["pdf", "docx", "txt", "csv"]);
const FRESHNESS_TONE = {
  current_evidence: "border-[#A8C6B5] bg-[#E8F0EB] text-[#1E3F33]",
  still_useful: "border-[#B6CBC0] bg-[#EFF4F1] text-[#1E3F33]",
  older_context: "border-[#E9D9A9] bg-[#FDF8F3] text-[#9C6B2E]",
  background_only: "border-[#E9C7B4] bg-[#FBEFE6] text-[#8A4A21]",
  date_not_provided: "border-[#D6D3CB] bg-[#F1F2F4] text-[#6B7280]",
};

function AISuggestionsModal({ studentId, needAreas, onClose, onAccepted, initialRunIds = [] }) {
  const [manualCtx, setManualCtx] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [acceptingId, setAcceptingId] = useState("");
  // FP3-034A — provider chooses which docs the AI reads.
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [selectedDocIds, setSelectedDocIds] = useState(new Set());
  // FP3-035 correction — provider picks assessment runs too.
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [selectedRunIds, setSelectedRunIds] = useState(
    () => new Set(initialRunIds || [])
  );
  // FP3-034B — Per-doc extraction status from the preview endpoint
  // (keyed by document_id). Lets the picker tell providers BEFORE
  // they hit Generate which docs the AI can actually read.
  const [extractionStatus, setExtractionStatus] = useState({});
  const [extractionLoading, setExtractionLoading] = useState(false);
  // FP3-034B — Structured error from ai-suggest. Replaces the
  // unhelpful "AI suggestion failed" toast with a friendly in-modal
  // banner that says exactly what went wrong + what to do.
  const [aiError, setAiError] = useState(null);

  // FP3-034B correction — Goal Plan AI picker omits the
  // evidence_scope filter, so the provider sees BOTH the general
  // Documents pool AND the Assessment artifacts pool as selectable
  // evidence. The Documents tab and Assessments tab each filter to
  // their own scope; here we want everything.
  useEffect(() => {
    let cancelled = false;
    setDocsLoading(true);
    api.listStudentDocuments(studentId)
      .then((r) => {
        if (cancelled) return;
        setDocs((r.data?.items || []).filter((d) => d.status !== "archived"));
      })
      .catch(() => { if (!cancelled) setDocs([]); })
      .finally(() => { if (!cancelled) setDocsLoading(false); });
    return () => { cancelled = true; };
  }, [studentId]);

  // FP3-035 correction — Completed Assessment Runner records sit
  // beside documents in the same picker. Drafts are excluded; the
  // AI only reads completed administrations.
  useEffect(() => {
    let cancelled = false;
    setRunsLoading(true);
    api.listAssessmentRuns(studentId, { status: "completed" })
      .then((r) => {
        if (cancelled) return;
        setRuns(r.data?.items || []);
      })
      .catch(() => { if (!cancelled) setRuns([]); })
      .finally(() => { if (!cancelled) setRunsLoading(false); });
    return () => { cancelled = true; };
  }, [studentId]);

  // FP3-034B — When the provider toggles documents, run a quick
  // (non-LLM) extraction preview so each row shows analyzable /
  // chars-extracted / no-text BEFORE Generate is clicked.
  useEffect(() => {
    let cancelled = false;
    const ids = Array.from(selectedDocIds);
    if (!ids.length) {
      setExtractionStatus({});
      return () => {};
    }
    setExtractionLoading(true);
    api
      .previewGoalPlanExtraction(studentId, { document_ids: ids })
      .then((r) => {
        if (cancelled) return;
        const next = {};
        for (const row of r.data?.documents_used || []) {
          next[row.document_id] = row;
        }
        setExtractionStatus(next);
      })
      .catch(() => {
        // Picker should still let the user proceed — they'll get the
        // full error from ai-suggest if extraction truly fails.
        if (!cancelled) setExtractionStatus({});
      })
      .finally(() => { if (!cancelled) setExtractionLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, selectedDocIds.size, Array.from(selectedDocIds).sort().join(",")]);

  const docsById = useMemo(() => {
    const m = {};
    for (const d of docs) m[d.id] = d;
    // Also map any documents_used returned by the AI (so we can
    // show titles even if a doc was deleted between selection and
    // accept).
    for (const u of suggestion?.documents_used || []) {
      if (!m[u.document_id]) m[u.document_id] = {
        id: u.document_id,
        document_title: u.title,
        document_type: u.type,
        document_date: u.date,
        evidence_freshness: u.freshness,
        file_extension: u.extension,
      };
    }
    return m;
  }, [docs, suggestion]);

  // FP3-035 correction — Manifest lookup for assessment runs the AI
  // cited. Prefers the freshest data from `runs` (live picker) but
  // falls back to `suggestion.assessment_runs_used` so a target that
  // references a now-archived run still renders nicely.
  const runsById = useMemo(() => {
    const m = {};
    for (const r of runs) m[r.id] = r;
    for (const u of suggestion?.assessment_runs_used || []) {
      if (!m[u.run_id]) m[u.run_id] = {
        id: u.run_id,
        title: u.title,
        assessment_type: u.assessment_type,
        skill_area: u.skill_area,
        date_administered: u.date_administered,
        summary: u.summary,
      };
    }
    return m;
  }, [runs, suggestion]);

  const toggleDoc = (id) => {
    setSelectedDocIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // FP3-035 correction — Same toggle pattern for assessment runs.
  const toggleRun = (id) => {
    setSelectedRunIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const generate = async () => {
    setLoading(true);
    setAiError(null);
    setSuggestion(null);
    try {
      const r = await api.aiSuggestGoalPlan(studentId, {
        manual_context: manualCtx,
        document_ids: Array.from(selectedDocIds),
        assessment_run_ids: Array.from(selectedRunIds),
      });
      setSuggestion(r.data);
    } catch (err) {
      // FP3-034B — Backend now returns structured detail objects:
      //   { error_code, message, raw?, documents_used?, reasons? }
      // Fall back to legacy string detail for safety.
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status || 0;
      if (detail && typeof detail === "object" && detail.message) {
        setAiError({
          code: detail.error_code || "ai_upstream_error",
          message: detail.message,
          raw: detail.raw || "",
          documents_used: detail.documents_used || [],
          reasons: detail.reasons || [],
          status,
        });
      } else if (typeof detail === "string" && detail.trim()) {
        setAiError({
          code: "ai_upstream_error",
          message: detail,
          raw: "",
          documents_used: [],
          reasons: [],
          status,
        });
      } else if (err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) {
        // Client-side timeout (axios timeout=300s for ai-suggest).
        setAiError({
          code: "ai_timeout",
          message:
            "The AI took too long to respond. Try Generate again — if this keeps happening, reduce the number of selected documents.",
          raw: err?.message || "",
          documents_used: [],
          reasons: [],
          status: 0,
        });
      } else if (status === 502 || status === 504 || status === 503) {
        // FP3-034B correction — Edge proxy returned a generic
        // 5xx with no JSON body. That almost always means the
        // upstream (Claude/LiteLLM) is in a retry storm or
        // out of budget and the proxy gave up before our backend
        // classifier could respond. Surface a USEFUL message
        // rather than the misleading "Check your network".
        setAiError({
          code: "ai_unavailable",
          message:
            "The AI service didn’t respond in time. This usually means Claude is rate-limited, overloaded, or the Universal Key budget is exhausted. Wait a minute and Try again. If it keeps failing, top up the key (Profile → Universal Key → Add Balance) or reduce the number of selected documents.",
          raw: `HTTP ${status}`,
          documents_used: [],
          reasons: [],
          status,
        });
      } else if (status === 0 || !err?.response) {
        setAiError({
          code: "ai_unavailable",
          message:
            "Couldn’t reach the AI service. Check your network connection and try Generate again.",
          raw: err?.message || "",
          documents_used: [],
          reasons: [],
          status: 0,
        });
      } else {
        setAiError({
          code: "ai_unknown",
          message: `The AI service returned an unexpected response (HTTP ${status}). Try again, and contact support if it persists.`,
          raw: err?.message || "",
          documents_used: [],
          reasons: [],
          status,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const acceptTarget = async (sug, key) => {
    setAcceptingId(key);
    try {
      const matchedNa = needAreas.find(
        (na) => na.label.toLowerCase() === (sug.need_area_label || "").toLowerCase()
      );
      await api.createGoalPlanTarget(studentId, {
        label: sug.label,
        target_skill: sug.target_skill,
        status: sug.status || "suggested",
        rationale: sug.rationale || "",
        instructional_level: sug.instructional_level || "",
        need_area_id: matchedNa ? matchedNa.id : null,
        // FP3-034A — preserve the source-document trace so the
        // accepted target shows "linked document" badges from day
        // one.
        related_document_ids: Array.isArray(sug.source_document_ids)
          ? sug.source_document_ids
          : [],
        // FP3-035 correction — same idea for assessment runs.
        related_assessment_run_ids: Array.isArray(sug.source_assessment_run_ids)
          ? sug.source_assessment_run_ids
          : [],
        next_step_options: Array.isArray(sug.next_step_options)
          ? sug.next_step_options
          : [],
      });
      toast.success("Suggestion added");
      await onAccepted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add");
    } finally {
      setAcceptingId("");
    }
  };

  const acceptNeedArea = async (sug, key) => {
    setAcceptingId(key);
    try {
      await api.createGoalPlanNeedArea(studentId, {
        label: sug.label,
        evidence_summary: sug.evidence_summary || sug.rationale || "",
        description: sug.rationale || "",
        priority: sug.priority || "medium",
        provider_domain: sug.provider_domain || "other",
      });
      toast.success("Long-term area added");
      await onAccepted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add");
    } finally {
      setAcceptingId("");
    }
  };

  const analyzableSelectedCount = Array.from(selectedDocIds).filter(
    (id) => docsById[id] && ANALYZABLE_EXT.has((docsById[id].file_extension || "").toLowerCase())
  ).length;

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid="goal-plan-ai-modal"
        aria-describedby={undefined}
        className="max-w-2xl max-h-[90vh] overflow-y-auto p-0 border-[#E5E3DB] rounded-2xl"
      >
        <header className="px-5 pt-5 pb-3 border-b border-[#F1F2F4]">
          <DialogTitle className="text-[15px] font-semibold text-[#1F2937] heading-font flex items-center gap-2">
            <Sparkle size={14} weight="duotone" className="text-[#1E3F33]" />
            Suggest from evidence
          </DialogTitle>
          <p
            className="text-[11.5px] text-[#6B7280] mt-1"
            data-testid="goal-plan-ai-helper-copy"
          >
            AI reviews the documents AND assessment results you
            select, active goals, and progress summaries to suggest
            draft Goal Plan need areas and targets. Review and edit
            every suggestion before saving — AI does not determine
            eligibility, diagnose, or create goals automatically.
          </p>
        </header>
        <div className="px-5 py-4 space-y-3.5">
          {/* Document picker */}
          <div data-testid="goal-plan-ai-doc-picker">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span className="block text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold">
                Documents to analyze
              </span>
              <span
                data-testid="goal-plan-ai-selected-count"
                className="text-[10.5px] text-[#6B7280]"
              >
                {selectedDocIds.size} selected
                {selectedDocIds.size > 0 &&
                  selectedDocIds.size !== analyzableSelectedCount &&
                  ` · ${analyzableSelectedCount} analyzable`}
              </span>
            </div>
            {docsLoading ? (
              <p className="text-[12px] text-[#6B7280]">Loading documents…</p>
            ) : docs.length === 0 ? (
              <p
                className="text-[12px] text-[#6B7280]"
                data-testid="goal-plan-ai-no-docs"
              >
                No documents on file. Upload an evaluation or progress
                report on the Documents tab first to ground the
                planner in evidence.
              </p>
            ) : (
              <div className="space-y-1.5 max-h-[200px] overflow-y-auto pr-1">
                {docs.map((d) => {
                  const ext = (d.file_extension || "").toLowerCase();
                  const analyzable = ANALYZABLE_EXT.has(ext);
                  const checked = selectedDocIds.has(d.id);
                  const tone = FRESHNESS_TONE[d.evidence_freshness] || FRESHNESS_TONE.date_not_provided;
                  // FP3-034B — Real extraction status from the preview
                  // endpoint (kind: selectable | no_text | unsupported
                  // | forbidden | error). Only shown for rows the
                  // provider has selected, since that's where the
                  // probe actually ran.
                  const probe = checked ? extractionStatus[d.id] : null;
                  return (
                    <label
                      key={d.id}
                      data-testid={`goal-plan-ai-doc-row-${d.id}`}
                      className={`flex items-start gap-2 px-2.5 py-2 rounded-md border ${
                        checked ? "border-[#1E3F33] bg-[#E8F0EB]" : "border-[#E5E3DB] bg-white"
                      } ${!analyzable ? "opacity-70" : "cursor-pointer hover:border-[#D8D5CC]"}`}
                    >
                      <input
                        type="checkbox"
                        data-testid={`goal-plan-ai-doc-check-${d.id}`}
                        checked={checked}
                        disabled={!analyzable}
                        onChange={() => toggleDoc(d.id)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[12.5px] font-medium text-[#1F2937] truncate">
                            {d.document_title}
                          </span>
                          <span className="font-mono text-[10px] uppercase text-[#6B7280]">
                            .{ext || "?"}
                          </span>
                          {!analyzable && (
                            <span className="text-[10px] text-[#9C6B2E]">
                              not analyzable
                            </span>
                          )}
                          {/* FP3-034B — Live extraction status pill */}
                          {checked && analyzable && (
                            <ExtractionStatusPill
                              probe={probe}
                              loading={extractionLoading && !probe}
                              testid={`goal-plan-ai-doc-extract-status-${d.id}`}
                            />
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap mt-0.5 text-[10.5px] text-[#6B7280]">
                          <span>{d.document_type}</span>
                          {d.document_date && <>· <span>{d.document_date}</span></>}
                          {/* FP3-034B correction — surface which pool
                              (Documents vs Assessments) the doc came
                              from so providers know they can select
                              evidence from BOTH tabs here. */}
                          {d.evidence_scope === "assessment" && (
                            <span
                              data-testid={`goal-plan-ai-doc-scope-${d.id}`}
                              className="uppercase tracking-[0.06em] font-semibold rounded-full px-1.5 py-0.5 border bg-[#EEF2FF] text-[#3F4756] border-[#C7D2EA]"
                            >
                              Assessment
                            </span>
                          )}
                          <span
                            className={`uppercase tracking-[0.06em] font-semibold rounded-full px-1.5 py-0.5 border ${tone}`}
                          >
                            {(d.evidence_freshness || "date_not_provided").replace(/_/g, " ")}
                          </span>
                        </div>
                        {/* FP3-034B — Per-doc warning right where the
                            provider made the selection, so they
                            understand which file blocked Generate. */}
                        {checked && probe?.warning && probe?.kind !== "selectable" && (
                          <p
                            className="text-[10.5px] text-[#9C6B2E] mt-1"
                            data-testid={`goal-plan-ai-doc-warning-${d.id}`}
                          >
                            {probe.warning}
                          </p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* FP3-035 correction — Assessment results picker. Sits
              beside the documents picker so providers can select
              completed Assessment Runner records as evidence on
              equal footing with uploaded reports. The AI prompt
              only ever sees the SUMMARY of these (title, type,
              skill area, date, score rollup, clinical summary,
              recommendations) — never raw item prompts. */}
          <div data-testid="goal-plan-ai-runs-picker">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span className="block text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold">
                Assessment results to analyze
              </span>
              <span
                data-testid="goal-plan-ai-runs-selected-count"
                className="text-[10.5px] text-[#6B7280]"
              >
                {selectedRunIds.size} selected
              </span>
            </div>
            {runsLoading ? (
              <p className="text-[12px] text-[#6B7280]">Loading assessment runs…</p>
            ) : runs.length === 0 ? (
              <p
                className="text-[12px] text-[#6B7280]"
                data-testid="goal-plan-ai-no-runs"
              >
                No completed assessment runs yet. Administer a probe
                from the Assessments tab to surface its results here
                as evidence.
              </p>
            ) : (
              <div className="space-y-1.5 max-h-[200px] overflow-y-auto pr-1">
                {runs.map((r) => {
                  const checked = selectedRunIds.has(r.id);
                  const s = r.summary || {};
                  const metrics = [];
                  if (s.percent_accuracy != null) metrics.push(`${s.percent_accuracy}% accuracy`);
                  if (s.average_rating != null) metrics.push(`${s.average_rating}/5 avg`);
                  if (s.trials_per_minute != null) metrics.push(`${s.trials_per_minute} tpm`);
                  if (s.correct_count != null && s.total_count) {
                    metrics.push(`${s.correct_count}/${s.total_count}`);
                  }
                  return (
                    <label
                      key={r.id}
                      data-testid={`goal-plan-ai-run-row-${r.id}`}
                      className={`flex items-start gap-2 px-2 py-1.5 rounded-md border transition cursor-pointer ${
                        checked
                          ? "border-[#1E3F33]/40 bg-[#F1F7F2]"
                          : "border-[#E5E3DB] bg-white hover:border-[#9B9890]"
                      }`}
                    >
                      <input
                        type="checkbox"
                        data-testid={`goal-plan-ai-run-toggle-${r.id}`}
                        checked={checked}
                        onChange={() => toggleRun(r.id)}
                        className="mt-1 accent-[#1E3F33]"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[12.5px] font-medium text-[#1F2937] truncate">
                            {r.title || "(untitled)"}
                          </span>
                          <span className="text-[10px] uppercase tracking-wide text-[#1E3F33] bg-[#E8F0EB] px-1.5 py-0.5 rounded">
                            {r.assessment_type === "skill_probe"
                              ? "Skill Probe"
                              : r.assessment_type === "rubric_rating"
                                ? "Rubric"
                                : "Timed Trial"}
                          </span>
                          <span className="text-[10.5px] text-[#6B7280]">
                            · {r.date_administered}
                          </span>
                        </div>
                        <p className="text-[10.5px] text-[#6B7280] mt-0.5">
                          {r.skill_area || "—"}
                          {metrics.length > 0 ? ` · ${metrics.join(" · ")}` : ""}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <Field label="Optional context for AI">
            <textarea
              data-testid="goal-plan-ai-context"
              value={manualCtx}
              onChange={(e) => setManualCtx(e.target.value)}
              rows={3}
              placeholder="e.g. parent reports difficulty with /s/ in conversation; team wants to keep /k,g/ as current focus through end of quarter"
              className="w-full text-[12.5px] border border-[#E5E3DB] rounded-md px-2 py-1.5"
            />
          </Field>
          <Button
            type="button"
            data-testid="goal-plan-ai-generate"
            size="sm"
            onClick={generate}
            disabled={loading}
            className="h-8 text-[12px] bg-[#1E3F33] hover:bg-[#173127] text-white"
          >
            <Sparkle size={12} className="mr-1" weight="duotone" />
            {loading
              ? "Generating…"
              : selectedDocIds.size + selectedRunIds.size > 0
                ? `Generate from ${selectedDocIds.size + selectedRunIds.size} item${selectedDocIds.size + selectedRunIds.size === 1 ? "" : "s"}`
                : suggestion
                  ? "Regenerate"
                  : "Generate suggestions"}
          </Button>

          {/* FP3-034B — Specific, actionable error banner instead of
              the generic "AI suggestion failed" toast. */}
          {aiError && !loading && (
            <AiErrorBanner
              error={aiError}
              onRetry={generate}
              onDismiss={() => setAiError(null)}
            />
          )}

          {suggestion && (
            <div className="space-y-4 pt-2" data-testid="goal-plan-ai-result">
              {/* FP3-034A — Cautions banner (truncation, no-text,
                  older context). */}
              {suggestion.cautions?.length > 0 && (
                <div
                  data-testid="goal-plan-ai-cautions"
                  className="rounded-md border border-[#E9D9A9] bg-[#FDF8F3] text-[#9C6B2E] p-3"
                >
                  <div className="text-[10.5px] uppercase tracking-[0.08em] font-semibold mb-1">
                    Cautions
                  </div>
                  <ul className="text-[11.5px] space-y-0.5 list-disc list-inside">
                    {suggestion.cautions.map((c, i) => (
                      <li key={i}>{c}</li>
                    ))}
                  </ul>
                </div>
              )}
              {/* FP3-034A — Documents-analyzed manifest. */}
              {suggestion.documents_used?.length > 0 && (
                <div
                  data-testid="goal-plan-ai-docs-used"
                  className="rounded-md border border-[#E5E3DB] bg-white p-3"
                >
                  <div className="text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold mb-1">
                    Documents analyzed
                  </div>
                  <ul className="text-[11.5px] text-[#1F2937] space-y-0.5">
                    {suggestion.documents_used.map((u) => (
                      <li key={u.document_id}>
                        <span className="font-medium">{u.title}</span>
                        <span className="text-[#6B7280] ml-1">
                          · {u.kind === "selectable"
                            ? `${u.chars} chars analyzed${u.truncated ? " (truncated)" : ""}`
                            : u.kind === "no_text"
                              ? "no extractable text"
                              : u.kind === "unsupported"
                                ? "file type not analyzable"
                                : u.kind === "forbidden"
                                  ? "no access"
                                  : "error"}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* FP3-035 correction — Assessment results manifest. */}
              {suggestion.assessment_runs_used?.length > 0 && (
                <div
                  data-testid="goal-plan-ai-runs-used"
                  className="rounded-md border border-[#1E3F33]/20 bg-[#F1F7F2] p-3"
                >
                  <div className="text-[10.5px] uppercase tracking-[0.08em] text-[#1E3F33] font-semibold mb-1">
                    Assessment results analyzed
                  </div>
                  <ul className="text-[11.5px] text-[#1F2937] space-y-0.5">
                    {suggestion.assessment_runs_used.map((u) => {
                      const s = u.summary || {};
                      const bits = [];
                      if (s.percent_accuracy != null) bits.push(`${s.percent_accuracy}% accuracy`);
                      if (s.average_rating != null) bits.push(`${s.average_rating}/5 avg`);
                      if (s.trials_per_minute != null) bits.push(`${s.trials_per_minute} tpm`);
                      return (
                        <li key={u.run_id}>
                          <span className="font-medium">{u.title}</span>
                          <span className="text-[#6B7280] ml-1">
                            · {u.assessment_type === "skill_probe"
                              ? "Skill Probe"
                              : u.assessment_type === "rubric_rating"
                                ? "Rubric"
                                : "Timed Trial"}
                            {u.skill_area ? ` · ${u.skill_area}` : ""}
                            {bits.length ? ` · ${bits.join(" · ")}` : ""}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {suggestion.summary && (
                <div className="rounded-md border border-[#E5E3DB] bg-[#FBFAF6] p-3">
                  <div className="text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold">
                    Summary
                  </div>
                  <p className="text-[12px] text-[#1F2937] mt-1">
                    {suggestion.summary}
                  </p>
                </div>
              )}
              {suggestion.need_areas?.length > 0 && (
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold mb-2">
                    Suggested long-term development areas
                  </div>
                  <div className="space-y-2">
                    {suggestion.need_areas.map((s, i) => {
                      const key = `na-${i}`;
                      return (
                        <div
                          key={key}
                          data-testid={`goal-plan-ai-need-area-${i}`}
                          className="rounded-md border border-[#E5E3DB] bg-white p-3 flex items-start gap-3"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-semibold text-[#1F2937]">
                              {s.label}
                            </div>
                            {s.rationale && (
                              <p className="text-[11.5px] text-[#4B5563] mt-1">
                                {s.rationale}
                              </p>
                            )}
                            {s.source_document_ids?.length > 0 && (
                              <SourceDocRow
                                ids={s.source_document_ids}
                                docsById={docsById}
                                testid={`goal-plan-ai-need-area-source-${i}`}
                              />
                            )}
                            {s.source_assessment_run_ids?.length > 0 && (
                              <SourceRunRow
                                ids={s.source_assessment_run_ids}
                                runsById={runsById}
                                testid={`goal-plan-ai-need-area-run-source-${i}`}
                              />
                            )}
                            <span className="text-[10px] uppercase tracking-[0.06em] font-semibold text-[#6B7280]">
                              {s.priority} priority
                            </span>
                          </div>
                          <Button
                            type="button"
                            data-testid={`goal-plan-ai-accept-need-area-${i}`}
                            size="sm"
                            variant="outline"
                            onClick={() => acceptNeedArea(s, key)}
                            disabled={acceptingId === key}
                            className="h-7 text-[11px] border-[#E5E3DB]"
                          >
                            {acceptingId === key ? "Adding…" : "Accept"}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {suggestion.targets?.length > 0 && (
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold mb-2">
                    Suggested targets
                  </div>
                  <div className="space-y-2">
                    {suggestion.targets.map((s, i) => {
                      const key = `t-${i}`;
                      const tone = TARGET_STATUS_TONE[s.status] || TARGET_STATUS_TONE.suggested;
                      return (
                        <div
                          key={key}
                          data-testid={`goal-plan-ai-target-${i}`}
                          className="rounded-md border border-[#E5E3DB] bg-white p-3 flex items-start gap-3"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span
                                className={`text-[10px] uppercase tracking-[0.06em] font-semibold rounded-full px-1.5 py-0.5 border ${tone}`}
                              >
                                {TARGET_STATUS_LABEL[s.status] || s.status}
                              </span>
                              {s.need_area_label && (
                                <span className="text-[10px] text-[#6B7280]">
                                  · {s.need_area_label}
                                </span>
                              )}
                            </div>
                            <div className="text-[13px] font-semibold text-[#1F2937] mt-1">
                              {s.label}
                            </div>
                            <p className="text-[11.5px] text-[#4B5563] mt-0.5">
                              <span className="text-[#6B7280] mr-1">Skill:</span>
                              {s.target_skill}
                            </p>
                            {s.recommended_measurement && (
                              <p className="text-[11.5px] text-[#4B5563] mt-0.5">
                                <span className="text-[#6B7280] mr-1">Measurement:</span>
                                {s.recommended_measurement}
                              </p>
                            )}
                            {s.rationale && (
                              <p className="text-[11.5px] text-[#4B5563] mt-1 italic">
                                {s.rationale}
                              </p>
                            )}
                            {s.source_document_ids?.length > 0 && (
                              <SourceDocRow
                                ids={s.source_document_ids}
                                docsById={docsById}
                                testid={`goal-plan-ai-target-source-${i}`}
                              />
                            )}
                            {s.source_assessment_run_ids?.length > 0 && (
                              <SourceRunRow
                                ids={s.source_assessment_run_ids}
                                runsById={runsById}
                                testid={`goal-plan-ai-target-run-source-${i}`}
                              />
                            )}
                          </div>
                          <Button
                            type="button"
                            data-testid={`goal-plan-ai-accept-target-${i}`}
                            size="sm"
                            variant="outline"
                            onClick={() => acceptTarget(s, key)}
                            disabled={acceptingId === key}
                            className="h-7 text-[11px] border-[#E5E3DB]"
                          >
                            {acceptingId === key ? "Adding…" : "Accept"}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {suggestion.need_areas?.length === 0 &&
                suggestion.targets?.length === 0 && (
                  <p
                    className="text-[12px] text-[#6B7280] text-center py-3"
                    data-testid="goal-plan-ai-empty"
                  >
                    The AI didn’t suggest anything specific — try selecting
                    more documents or giving more context above and
                    regenerating.
                  </p>
                )}
            </div>
          )}
        </div>
        <footer className="px-5 py-3 border-t border-[#F1F2F4] flex justify-end">
          <Button
            type="button"
            data-testid="goal-plan-ai-close"
            variant="outline"
            size="sm"
            onClick={onClose}
            className="h-8 text-[12px] border-[#E5E3DB]"
          >
            Done
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
}

function SourceDocRow({ ids, docsById, testid }) {
  if (!ids || ids.length === 0) return null;
  return (
    <p
      data-testid={testid}
      className="text-[10.5px] text-[#6B7280] mt-1 flex items-center gap-1 flex-wrap"
    >
      <LinkIcon size={10} />
      <span className="uppercase tracking-[0.06em] font-semibold">Source:</span>
      {ids.map((id, i) => {
        const d = docsById?.[id];
        const label = d?.document_title || id.slice(0, 8);
        const fresh = d?.evidence_freshness ? ` (${d.evidence_freshness.replace(/_/g, " ")})` : "";
        return (
          <span key={id}>
            {label}{fresh}
            {i < ids.length - 1 ? "," : ""}
          </span>
        );
      })}
    </p>
  );
}

// FP3-035 correction — Linked-assessment-evidence row. Mirrors
// SourceDocRow so the AI's `source_assessment_run_ids` are visible
// on each suggestion. `runsById` is the manifest the modal builds
// from `suggestion.assessment_runs_used`.
function SourceRunRow({ ids, runsById, testid }) {
  if (!ids || ids.length === 0) return null;
  return (
    <p
      data-testid={testid}
      className="text-[10.5px] text-[#1E3F33] mt-1 flex items-center gap-1 flex-wrap"
    >
      <Sparkle size={10} weight="duotone" />
      <span className="uppercase tracking-[0.06em] font-semibold">
        Assessment:
      </span>
      {ids.map((id, i) => {
        const r = runsById?.[id];
        if (!r) {
          return (
            <span key={id} className="text-[#6B7280]">
              {id.slice(0, 8)}{i < ids.length - 1 ? "," : ""}
            </span>
          );
        }
        const s = r.summary || {};
        let scoreBit = "";
        if (s.percent_accuracy != null) scoreBit = ` — ${s.percent_accuracy}% accuracy`;
        else if (s.average_rating != null) scoreBit = ` — ${s.average_rating}/5 avg`;
        else if (s.trials_per_minute != null) scoreBit = ` — ${s.trials_per_minute} tpm`;
        const typeLabel =
          r.assessment_type === "skill_probe"
            ? "Skill Probe"
            : r.assessment_type === "rubric_rating"
              ? "Rubric"
              : "Timed Trial";
        return (
          <span key={id}>
            {typeLabel}{scoreBit}
            {i < ids.length - 1 ? "," : ""}
          </span>
        );
      })}
    </p>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold mb-1">
        {label}
        {required && <span className="text-[#B85C5C] ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}


// FP3-034B — Per-doc extraction status pill rendered in the AI
// modal's document picker once a doc is selected. The pill tells the
// provider whether the AI can actually read the file BEFORE they
// click Generate.
function ExtractionStatusPill({ probe, loading, testid }) {
  if (loading) {
    return (
      <span
        data-testid={testid}
        className="text-[10px] uppercase tracking-[0.06em] font-semibold rounded-full px-1.5 py-0.5 border bg-[#F1F2F4] text-[#6B7280] border-[#E5E3DB]"
      >
        Checking…
      </span>
    );
  }
  if (!probe) return null;
  const kind = probe.kind;
  if (kind === "selectable") {
    const label = probe.truncated
      ? `${probe.chars.toLocaleString()} chars (truncated)`
      : `${probe.chars.toLocaleString()} chars extractable`;
    return (
      <span
        data-testid={testid}
        data-extract-status="selectable"
        className="text-[10px] uppercase tracking-[0.06em] font-semibold rounded-full px-1.5 py-0.5 border bg-[#E8F0EB] text-[#1E3F33] border-[#A8C6B5]"
      >
        {label}
      </span>
    );
  }
  if (kind === "no_text") {
    return (
      <span
        data-testid={testid}
        data-extract-status="no_text"
        className="text-[10px] uppercase tracking-[0.06em] font-semibold rounded-full px-1.5 py-0.5 border bg-[#FDF8F3] text-[#9C6B2E] border-[#E9D9A9]"
      >
        No selectable text
      </span>
    );
  }
  if (kind === "unsupported") {
    return (
      <span
        data-testid={testid}
        data-extract-status="unsupported"
        className="text-[10px] uppercase tracking-[0.06em] font-semibold rounded-full px-1.5 py-0.5 border bg-[#F1F2F4] text-[#6B7280] border-[#E5E3DB]"
      >
        File type not analyzable
      </span>
    );
  }
  if (kind === "forbidden") {
    return (
      <span
        data-testid={testid}
        data-extract-status="forbidden"
        className="text-[10px] uppercase tracking-[0.06em] font-semibold rounded-full px-1.5 py-0.5 border bg-[#FBEFE6] text-[#8A4A21] border-[#E9C7B4]"
      >
        No access
      </span>
    );
  }
  return (
    <span
      data-testid={testid}
      data-extract-status="error"
      className="text-[10px] uppercase tracking-[0.06em] font-semibold rounded-full px-1.5 py-0.5 border bg-[#FBEFE6] text-[#8A4A21] border-[#E9C7B4]"
    >
      Extraction failed
    </span>
  );
}

// FP3-034B — Friendly, action-oriented error banner that replaces the
// opaque "AI suggestion failed: litellm.BadRequestError…" toast.
function AiErrorBanner({ error, onRetry, onDismiss }) {
  if (!error) return null;
  const code = error.code || "ai_unknown";
  const tone =
    code === "budget_exhausted"
      ? "border-[#E9C7B4] bg-[#FBEFE6] text-[#7A3A2C]"
      : code === "no_extractable_text"
        ? "border-[#E9D9A9] bg-[#FDF8F3] text-[#7A5B1F]"
        : "border-[#E9C7B4] bg-[#FBEFE6] text-[#7A3A2C]";
  const title =
    code === "budget_exhausted"
      ? "Universal Key budget exhausted"
      : code === "rate_limited"
        ? "Claude is rate-limited"
        : code === "ai_timeout"
          ? "AI took too long"
          : code === "invalid_api_key"
            ? "Universal Key rejected"
            : code === "ai_unavailable"
              ? "AI service didn’t respond in time"
              : code === "missing_api_key"
                ? "Universal Key not configured"
                : code === "no_extractable_text"
                  ? "None of the selected documents had extractable text"
                  : "AI suggestion couldn’t complete";
  return (
    <div
      data-testid="goal-plan-ai-error"
      data-error-code={code}
      className={`rounded-md border ${tone} p-3 space-y-2`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[12px] uppercase tracking-[0.08em] font-semibold">
            {title}
          </div>
          <p
            className="text-[12px] mt-1"
            data-testid="goal-plan-ai-error-message"
          >
            {error.message}
          </p>
        </div>
        {onDismiss && (
          <button
            type="button"
            data-testid="goal-plan-ai-error-dismiss"
            onClick={onDismiss}
            className="text-[14px] leading-none px-1 opacity-70 hover:opacity-100"
            aria-label="Dismiss error"
          >
            ×
          </button>
        )}
      </div>
      {Array.isArray(error.reasons) && error.reasons.length > 0 && (
        <ul
          className="text-[11.5px] list-disc list-inside space-y-0.5"
          data-testid="goal-plan-ai-error-reasons"
        >
          {error.reasons.map((r, i) => (
            <li key={i}>{r}</li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2 pt-1">
        {onRetry && code !== "no_extractable_text" && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="goal-plan-ai-error-retry"
            onClick={onRetry}
            className="h-7 text-[11px] border-current bg-white/60 hover:bg-white"
          >
            Try again
          </Button>
        )}
        {code === "budget_exhausted" && (
          <span className="text-[11px] opacity-90">
            Profile → Universal Key → Add Balance
          </span>
        )}
      </div>
    </div>
  );
}
